import { logError } from '~shared/observability/observability';
import { Offer } from '~entities/offer/model/offer.types';
import { CartItem, LastBooking, Session } from '../model/session.types';
import {
  AddToCartDto,
  SessionDto,
  UpdateCartItemDto,
} from '~shared/api/api.types';
import { LastBookingDTO } from '~shared/api/schemas';
import { assertMoneyReported } from '~shared/api/assert-money';

export const transformSessionDtoToSession = ({
  items,
  ...sessionDto
}: SessionDto): Session => {
  return {
    ...sessionDto,
    cart: items.map(({ roomCategoryId, packageGroupId, ...offer }) => ({
      ...offer,
      roomId: roomCategoryId,
      packageId: packageGroupId,
    })),
    promotionCode: sessionDto.promotion?.code,
  };
};

export const transformOfferToAddToCartDto = (offer: Offer): AddToCartDto => {
  // TODO: in future no throw errors, make more type safe api
  if (
    !offer.type ||
    !offer.adults ||
    !offer.children ||
    !offer.price ||
    !offer.rateId ||
    !offer.roomId
  ) {
    const missingFields: string[] = [];
    if (!offer.type) missingFields.push('type');
    if (!offer.adults) missingFields.push('adults');
    if (!offer.children) missingFields.push('children');
    if (!offer.price) missingFields.push('price');
    if (!offer.rateId) missingFields.push('rateId');
    if (!offer.roomId) missingFields.push('roomId');

    const message = `Invalid offer: missing ${missingFields.join(', ')}`;
    logError(new Error(message), {
      // SigNoz has no fingerprint concept, so grouping happens at query time
      // on error.fingerprint. logError derives it from the message, which also
      // covers the unhandled rejection from the throw below — that arrives as
      // a separate record and would otherwise group by its own message.
      kind: 'invalid-offer',
      missingOfferFields: missingFields.join(','),
      offer: { ...offer } as Record<string, unknown>,
    });
    throw new Error(message);
  }

  return {
    type: offer.type,
    arrivalDay: offer.arrivalDay,
    nights: offer.nights,
    adults: offer.adults,
    children: offer.children,
    price: offer.price,

    rateId: offer.rateId,
    roomCategoryId: offer.roomId,

    lastMinutePackageId: offer.lastMinutePackageId,
    packageGroupId: offer.packageId,
    packageCategoryId: offer.packageCategoryId,
  };
};

export const transformCartItemToUpdateCartItemDto = ({
  roomId,
  ...cartItem
}: CartItem): UpdateCartItemDto => {
  return {
    ...cartItem,
    roomCategoryId: roomId,
  };
};

export const transformLastBookingDtoToLastBooking = (
  lastBookingDto: LastBookingDTO,
): LastBooking => {
  // This endpoint moved off the Zod responseContract to the typed client, which
  // only guarantees SHAPE at compile time — nothing checks the values on the
  // wire. Every money field here is user-facing AND feeds the GTM purchase event
  // (build-purchase-data-layer.ts), so an amount arriving as null or "129.00"
  // would silently become a wrong displayed price and a wrong conversion value.
  //
  // Reported explicitly rather than relying on the throw surfacing by itself:
  // the error boundary's production logError is still a TODO, so an unreported
  // contract break would be invisible. lastBookingQueryOptions additionally sets
  // throwOnError for this error type, so the boundary actually renders.
  assertMoneyReported(lastBookingDto.total, 'lastBooking.total');
  assertMoneyReported(
    lastBookingDto.depositAmount,
    'lastBooking.depositAmount',
  );
  lastBookingDto.items.forEach((item, index) =>
    assertMoneyReported(item.price, `lastBooking.items[${index}].price`),
  );

  return {
    ...lastBookingDto,
  };
};
